/**
 * CJAS — the four service request forms.  Jira CA-50, CA-27.
 *
 * Nikah, janazah, counselling, other. These are the screens where a member is
 * least likely to be calm and most likely to be on a phone in a hurry, so the
 * design rules are:
 *
 *   - ask for the minimum, and never for a fødselsnummer
 *   - validate in Norwegian, in plain words, next to the field
 *   - never lose a half-filled form
 *   - janazah is not a form to be perfected, it is a phone call with a record
 */

/**
 * CA-50 names four forms: nikah, omvisning, fatiha and kontakt. Janazah and
 * samtale-med-imam are here too because they exist in the requests model
 * (CA-27) and a member looking for "Tjenester" will look for them here.
 * Six schemas, one validator — adding a form is a schema entry, not code.
 */
const KINDS = ['nikah', 'janazah', 'tour', 'fatiha', 'counselling', 'other'];

/**
 * Fields nobody may add to these forms, whatever the reason given.
 * Kept in step with member-import.js FORBIDDEN — an ASCII-only list here
 * would have let the literal Norwegian spelling through.
 */
const FORBIDDEN_FIELDS = [
  'fodselsnummer', 'fnr', 'personnummer', 'personnr', 'fodselsnr',
  'ssn', 'national_id', 'birth_number',
];

const f = (name, type, label_no, opts = {}) => ({ name, type, label_no, required: false, ...opts });

const SCHEMAS = {
  /**
   * Janazah. Deliberately the shortest form in the app.
   *
   * A death has been announced and the family is arranging a burial today or
   * tomorrow. Four fields and a phone number the mosque can ring back on. Every
   * additional required field is a family filling in a form instead of grieving.
   */
  janazah: {
    kind: 'janazah',
    title_no: 'Janazah',
    urgent: true,
    intro_no: 'Ring moskeen direkte hvis det haster. Dette skjemaet sikrer at beskjeden også blir registrert.',
    showPhoneFirst: true,
    // Phone first, literally first in the array — showPhoneFirst is a claim
    // about this order, and a form that renders fields in order must honour it.
    // Someone filling this in has just lost a family member; if they abandon
    // the form halfway, we still want a number we can ring back.
    fields: [
      f('contact_phone', 'phone', 'Telefonnummer vi kan ringe', { required: true }),
      f('contact_name', 'text', 'Ditt navn', { required: true, max: 120 }),
      f('deceased_name', 'text', 'Avdødes navn', { required: true, max: 120 }),
      f('date_of_death', 'date', 'Dødsdato', { required: false, maxDate: 'today' }),
      f('note', 'textarea', 'Noe vi bør vite', { max: 1000 }),
    ],
    documents: [],
    autoAcknowledge_no: 'Vi har mottatt beskjeden og tar kontakt så snart som mulig.',
  },

  /**
   * Nikah. CJAS holds vigselsrett, so the three documents are a legal
   * requirement, not a preference — a nikah cannot be scheduled without them.
   */
  nikah: {
    kind: 'nikah',
    title_no: 'Nikah (vielse)',
    urgent: false,
    intro_no: 'Vi trenger prøvingsattest fra Skatteetaten før vi kan sette en dato. Den er gyldig i fire måneder.',
    fields: [
      f('party_a_name', 'text', 'Navn, part 1', { required: true, max: 120 }),
      f('party_b_name', 'text', 'Navn, part 2', { required: true, max: 120 }),
      f('contact_phone', 'phone', 'Telefonnummer', { required: true }),
      f('contact_email', 'email', 'E-post'),
      f('preferred_date', 'date', 'Ønsket dato', { required: true, minDate: 'today' }),
      f('alternative_date', 'date', 'Alternativ dato', { minDate: 'today' }),
      f('witnesses', 'number', 'Antall vitner', { min: 2, max: 10 }),
      f('note', 'textarea', 'Andre ønsker', { max: 1000 }),
    ],
    documents: [
      { id: 'provingsattest', label_no: 'Prøvingsattest fra Skatteetaten', required: true,
        note_no: 'Gyldig i fire måneder fra utstedelse.', retentionDays: 400 },
      { id: 'id_party_a', label_no: 'Legitimasjon, part 1', required: true, retentionDays: 400 },
      { id: 'id_party_b', label_no: 'Legitimasjon, part 2', required: true, retentionDays: 400 },
    ],
  },

  counselling: {
    kind: 'counselling',
    title_no: 'Samtale med imam',
    urgent: false,
    intro_no: 'Henvendelsen behandles fortrolig og leses kun av imam.',
    confidential: true,
    fields: [
      f('contact_name', 'text', 'Navn', { required: true, max: 120 }),
      f('contact_phone', 'phone', 'Telefonnummer', { required: true }),
      f('preferred_contact', 'select', 'Hvordan vil du bli kontaktet',
        { options: [['phone', 'Telefon'], ['sms', 'SMS'], ['in_person', 'Møte i moskeen']], required: true }),
      f('note', 'textarea', 'Kort om hva det gjelder', { max: 2000 }),
    ],
    documents: [],
  },

  /**
   * Omvisning. Usually a school class or a Norwegian institution, so the
   * group size and the age band are what the mosque needs to plan around —
   * a class of twenty nine-year-olds is a different visit to six adults.
   */
  tour: {
    kind: 'tour',
    title_no: 'Omvisning i moskeen',
    urgent: false,
    intro_no: 'Vi tar imot skoleklasser, studenter og grupper. Gi oss gjerne to ukers varsel.',
    fields: [
      f('contact_name', 'text', 'Kontaktperson', { required: true, max: 120 }),
      f('organisation', 'text', 'Skole eller organisasjon', { max: 160 }),
      f('contact_phone', 'phone', 'Telefonnummer', { required: true }),
      f('contact_email', 'email', 'E-post'),
      f('group_size', 'number', 'Antall personer', { required: true, min: 1, max: 200 }),
      f('age_group', 'select', 'Aldersgruppe',
        { options: [['children', 'Barn'], ['youth', 'Ungdom'], ['adults', 'Voksne'], ['mixed', 'Blandet']] }),
      f('preferred_date', 'date', 'Ønsket dato', { required: true, minDate: 'today' }),
      f('note', 'textarea', 'Er det noe spesielt dere ønsker å se eller høre om', { max: 1000 }),
    ],
    documents: [],
  },

  /**
   * Fatiha. A prayer for someone who has died, often long after the burial
   * and often for a relative buried abroad — so unlike janazah this is not
   * urgent, and the date of death is frequently not known precisely.
   */
  fatiha: {
    kind: 'fatiha',
    title_no: 'Fatiha',
    urgent: false,
    fields: [
      f('contact_name', 'text', 'Ditt navn', { required: true, max: 120 }),
      f('contact_phone', 'phone', 'Telefonnummer', { required: true }),
      f('deceased_name', 'text', 'Navn på avdøde', { required: true, max: 120 }),
      f('relation', 'text', 'Din relasjon til avdøde', { max: 120 }),
      f('preferred_date', 'date', 'Ønsket dato', { minDate: 'today' }),
      f('note', 'textarea', 'Noe vi bør vite', { max: 1000 }),
    ],
    documents: [],
  },

  other: {
    kind: 'other',
    title_no: 'Annen henvendelse',
    urgent: false,
    fields: [
      f('contact_name', 'text', 'Navn', { required: true, max: 120 }),
      f('contact_phone', 'phone', 'Telefonnummer', { required: true }),
      f('subject', 'text', 'Emne', { required: true, max: 160 }),
      f('note', 'textarea', 'Beskrivelse', { required: true, max: 2000 }),
    ],
    documents: [],
  },
};

function schema(kind) { return SCHEMAS[kind] || null; }


/**
 * Does this free text contain something that is probably a fødselsnummer?
 *
 * ADDED 2026-09-09 (mismatch 20). Rule 12 says a fødselsnummer never enters
 * the platform, and until now that was enforced on **field names only** —
 * `isForbiddenField()`. But nobody types a fødselsnummer into a field called
 * "fodselsnummer"; the mosque does not have one. They type it into
 * **"melding"**, on a nikah booking, because they believe it is needed for
 * the attest. That is the realistic path, and it was completely open: the
 * value went to the API, into `service_request`, and into the staff inbox.
 *
 * Detection is by the CONTROL DIGITS, not by "eleven digits in a row".
 * A Norwegian bank account number is also eleven digits, and refusing every
 * long number would make the contact form unusable while teaching people to
 * work around it. The mod-11 check makes a false positive very unlikely, and
 * a real fødselsnummer essentially always matches.
 *
 * Returns a boolean and NOTHING ELSE. It deliberately does not return the
 * matched value, its position, or a masked form of it: this function is
 * called on its way to being refused, and a helper that hands back the number
 * is a helper that will eventually be logged.
 */
const FNR_W1 = [3, 7, 6, 1, 8, 9, 4, 5, 2];
const FNR_W2 = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

/*
 * Synthetic test identities (Skatteetaten's Tenor test register, which Digipost's and ID-porten's test environments
 * use) carry month + 80. They belong to nobody and never occur in production, so they are refused unless a staging or
 * development process switched them on at start (allowSyntheticNumbers, from core deployment.kind). 2026-10-16.
 */
let syntheticAllowed = false;
function allowSyntheticNumbers(on) { syntheticAllowed = Boolean(on); }

function isFodselsnummer(digits) {
  if (!/^\d{11}$/.test(digits)) return false;
  const d = digits.split('').map(Number);

  // Day may carry +40 (D-number) and month +20 (some H-numbers). Accept the
  // real ranges rather than a strict calendar check — being slightly generous
  // here refuses slightly more, which is the safe direction for this rule.
  const day = d[0] * 10 + d[1];
  const month = d[2] * 10 + d[3];
  const realDay = day > 40 ? day - 40 : day;
  const realMonth = syntheticAllowed && month > 80 ? month - 80 : month > 20 ? month - 20 : month;
  if (realDay < 1 || realDay > 31 || realMonth < 1 || realMonth > 12) return false;

  const [k1, k2] = fnrControlDigits(d);
  return k1 === d[9] && k2 === d[10];
}

/**
 * The two mod-11 control digits a number's first nine digits call for: [k1, k2]. k2 is computed over the first nine
 * digits and the number's OWN tenth digit (as the rule says), so a wrong k1 does not hide a wrong k2. null where the rule
 * gives 10 (no valid number has those first digits). THE one implementation of the check digits: isFodselsnummer above
 * and core national-id.js (which says which digit is wrong) both use it. Takes digits as numbers; returns numbers only.
 */
function fnrControlDigits(d) {
  const check = (weights) => {
    let sum = 0;
    for (let i = 0; i < weights.length; i++) sum += weights[i] * d[i];
    const r = 11 - (sum % 11);
    const k = r === 11 ? 0 : r;
    return k === 10 ? null : k;
  };
  return [check(FNR_W1), check(FNR_W2)];
}

function containsFodselsnummer(text) {
  const s = String(text || '');
  // Spaces and a single separator are how people actually write it:
  // "010199 12345", "010199-12345". Strip those between digit runs first.
  const normalised = s.replace(/(\d)[\s.\-]+(?=\d)/g, '$1');
  const runs = normalised.match(/\d{11,}/g) || [];
  for (const run of runs) {
    // A longer run may contain one; slide across it.
    for (let i = 0; i + 11 <= run.length; i++) {
      if (isFodselsnummer(run.slice(i, i + 11))) return true;
    }
  }
  return false;
}

function isForbiddenField(name) {
  // Fold the Norwegian vowels before matching, so "Fødselsnummer" and
  // "fodselsnummer" are the same string to this check.
  const n = String(name || '').toLowerCase()
    .replace(/ø/g, 'o').replace(/å/g, 'a').replace(/æ/g, 'ae')
    .replace(/[_\s-]/g, '');
  return FORBIDDEN_FIELDS.some(x => n.includes(x.replace(/_/g, '')));
}

const iso = d => {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function normalisePhone(input) {
  if (!input) return null;
  let s = String(input).replace(/[\s\-().]/g, '');
  if (s.startsWith('00')) s = '+' + s.slice(2);
  if (!s.startsWith('+')) { if (!/^\d{8}$/.test(s)) return null; s = '+47' + s; }
  return /^\+\d{8,15}$/.test(s) ? s : null;
}

/**
 * Validate. Returns errors keyed by field so the message lands next to the
 * input rather than in a summary box at the top that a phone keyboard hides.
 */
/**
 * Every error this function can produce, as a code and its Norwegian text.
 *
 * ADDED 2026-09-09. `errors` was a map of field name → a Norwegian sentence,
 * and the app rendered those sentences directly. So the four service forms
 * spoke Norwegian in all three languages — field labels, hints, and, worst,
 * the validation messages, which are shown to somebody who has just made a
 * mistake and is least able to guess. Opening the nikah form with the app in
 * Urdu gave an Urdu back button above an entirely Norwegian form.
 *
 * The RULE stays here — which fields, which are required, what makes a value
 * invalid — and the WORDS move to build/i18n/strings.json, which is where
 * every other visitor-facing string in this product already lives. Core
 * returns both: `errors` keeps the Norwegian sentence so the admin panel, the
 * WordPress site and every existing test keep working unchanged, and
 * `errorCodes` carries `{ code, vars }` for anything that has a translator.
 */
const ERROR_NO = {
  required: (v) => `${v.label} må fylles ut.`,
  maxLength: (v) => `Maks ${v.max} tegn.`,
  phone: () => 'Skriv et gyldig telefonnummer, for eksempel 912 34 567.',
  email: () => 'E-postadressen ser ikke riktig ut.',
  date: () => 'Ugyldig dato.',
  datePast: () => 'Datoen kan ikke være i fortiden.',
  dateFuture: () => 'Datoen kan ikke være i framtiden.',
  number: () => 'Skriv et tall.',
  min: (v) => `Minst ${v.min}.`,
  max: (v) => `Høyst ${v.max}.`,
  select: () => 'Velg et alternativ.',
  unknownKind: () => 'Ukjent skjematype.',
  forbiddenField: () => 'Dette feltet kan ikke brukes. Fødselsnummer lagres aldri i plattformen.',
  // Never repeats the number back.
  fnrInText: () => 'Ta bort fødselsnummeret. Moskeen trenger det ikke, og det lagres aldri i plattformen.',
};

function validate(kind, values = {}, now = new Date()) {
  const s = schema(kind);
  if (!s) return { ok: false, errors: { _form: ERROR_NO.unknownKind() },
                   errorCodes: { _form: { code: 'unknownKind', vars: {} } } };

  const errors = {};
  const errorCodes = {};
  const today = iso(now);
  // One place an error is recorded, so the sentence and the code can never
  // describe different problems.
  const fail = (name, code, vars = {}) => {
    errors[name] = ERROR_NO[code](vars);
    errorCodes[name] = { code, vars };
  };

  for (const field of s.fields) {
    const raw = values[field.name];
    const v = typeof raw === 'string' ? raw.trim() : raw;

    if (field.required && (v === undefined || v === null || v === '')) {
      fail(field.name, 'required', { label: field.label_no, field: field.name });
      continue;
    }
    if (v === undefined || v === null || v === '') continue;

    if (field.max && String(v).length > field.max)
      fail(field.name, 'maxLength', { max: field.max });
    if (field.type === 'phone' && !normalisePhone(v))
      fail(field.name, 'phone');
    if (field.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v)))
      fail(field.name, 'email');
    if (field.type === 'date') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v))) fail(field.name, 'date');
      else if (field.minDate === 'today' && v < today) fail(field.name, 'datePast');
      else if (field.maxDate === 'today' && v > today) fail(field.name, 'dateFuture');
    }
    if (field.type === 'number') {
      const n = Number(v);
      if (!Number.isFinite(n)) fail(field.name, 'number');
      else if (field.min !== undefined && n < field.min) fail(field.name, 'min', { min: field.min });
      else if (field.max !== undefined && n > field.max) fail(field.name, 'max', { max: field.max });
    }
    if (field.type === 'select' && !field.options.some(o => o[0] === v))
      fail(field.name, 'select');
  }

  // A field nobody should have added. Caught here as well as in review,
  // because a well-meaning "we need the fnr for the attest" is exactly how
  // this would arrive.
  for (const k of Object.keys(values)) if (isForbiddenField(k))
    fail(k, 'forbiddenField');

  // And the way one actually arrives: typed into a free-text field by
  // somebody who believes it is needed. Refused, not stripped — a person who
  // is not told will assume it was received and that the mosque now has it.
  // The message never repeats the number back.
  for (const [k, v] of Object.entries(values)) {
    if (errors[k]) continue;
    if (typeof v === 'string' && containsFodselsnummer(v)) {
      fail(k, 'fnrInText');
    }
  }

  return { ok: Object.keys(errors).length === 0, errors, errorCodes };
}

/**
 * Can this be submitted yet?
 *
 * Nikah needs all three documents — vigselsrett means that is the law, not a
 * house rule. Everything else submits as soon as the fields are valid.
 */
function canSubmit(kind, values, uploaded = [], now = new Date()) {
  const v = validate(kind, values, now);
  if (!v.ok) return { ok: false, reason: 'validation', errors: v.errors };

  const s = schema(kind);
  const missing = s.documents.filter(d => d.required && !uploaded.includes(d.id));
  if (missing.length) {
    return { ok: false, reason: 'documents_missing',
      missing: missing.map(d => d.id),
      message_no: `Mangler: ${missing.map(d => d.label_no).join(', ')}.` };
  }
  return { ok: true };
}

/**
 * Build the request record. Note what is NOT here: no free-text field is
 * promoted into a searchable column, and the phone is normalised once so the
 * inbox can match it to a member without guessing.
 */
function toRequest(kind, values, uploaded = [], now = new Date()) {
  const s = schema(kind);
  const payload = {};
  for (const field of s.fields) {
    if (values[field.name] === undefined || values[field.name] === '') continue;
    payload[field.name] = field.type === 'phone'
      ? normalisePhone(values[field.name])
      : (typeof values[field.name] === 'string' ? values[field.name].trim() : values[field.name]);
  }
  return {
    kind, status: 'new',
    priority: s.urgent ? 'urgent' : 'normal',
    confidential: Boolean(s.confidential),
    payload,
    documents: uploaded,
    created_at: now.toISOString(),
    // Retention is set at upload so nobody has to remember to delete a
    // prøvingsattest four hundred days from now (CA-102).
    document_retention: Object.fromEntries(
      s.documents.filter(d => uploaded.includes(d.id))
        .map(d => [d.id, new Date(now.getTime() + d.retentionDays * 86400000).toISOString()])),
  };
}

/**
 * A half-filled form is saved locally and restored. A member who was
 * interrupted mid-nikah-form should not start again — and a draft that is
 * days old is more likely to confuse than help, so it expires.
 */
const DRAFT_EXPIRY_DAYS = 7;
function draft(kind, values, now = new Date()) {
  return { kind, values, savedAt: now.toISOString(),
           expiresAt: new Date(now.getTime() + DRAFT_EXPIRY_DAYS * 86400000).toISOString() };
}
function restoreDraft(d, now = new Date()) {
  if (!d || !d.expiresAt) return null;
  if (new Date(d.expiresAt) <= now) return null;
  return { kind: d.kind, values: d.values,
           ageHours: Math.floor((now - new Date(d.savedAt)) / 3600000) };
}

module.exports = {
  KINDS, SCHEMAS, FORBIDDEN_FIELDS, DRAFT_EXPIRY_DAYS, ERROR_NO,
  schema, isForbiddenField, isFodselsnummer, allowSyntheticNumbers, fnrControlDigits, containsFodselsnummer, normalisePhone,
  validate, canSubmit, toRequest, draft, restoreDraft,
};
